import { setBodyClasses, setUserAccessBodyProp } from '@hotjar/webapp/utils/document';
import { useNavigationStore } from '@hotjar/navigation';
import { useUIState } from '@hotjar/webapp/features/Core/hooks/useUIState';
import { bus, siteLastDataRefreshed, trackingCodeInstalled } from '@hotjar/webapp/utils/events';
import { mapSiteDtoToModel } from '@hotjar/site';
import { mapOwnedAccountDtoToModel } from '@hotjar/account';
import { startYearlyUpsell } from '@hotjar/checkout';
import { measureAndLog } from '@hotjar/performance';
import { MARKS } from '@hotjar/webapp/performance';

angular
    .module('hjApp')
    .controller(
        'AppCtrl',
        function (
            $q,
            $rootScope,
            $scope,
            $state,
            $timeout,
            Account,
            Organization,
            Plan,
            restDefaultHandlers,
            Page,
            Site,
            Subscription,
            Track,
            UiHelper,
            User,
            UserData,
            WindowTitle,
            Help,
            Contentsquare
        ) {
            const ownedAccount = Account.getOwned();

            /*************************
             * $ROOTSCOPE VARS
             *************************/
            $rootScope.UserData = UserData;

            /*************************
             * SCOPE VARIABLES
             *************************/
            $scope.model = {
                organizationUsers: [],
                sideBarMinimized: false,
                user: User.get(),
                account: ownedAccount
            };
            $scope.upsellReferrer = null;
            $scope.Contentsquare = Contentsquare;
            $scope.Help = Help;
            $scope.UiHelper = UiHelper;

            this.$onInit = () => {
                // Perf: Mark angular application render timings
                measureAndLog({
                    name: MARKS.APP_NG_RENDERED,
                    startMark: MARKS.APP_RENDERED,
                    addMark: true
                });
            };

            /*********************
             * PRIVATE FUNCTIONS
             **********************/

            /**
             * Initialize / handle any subscription related alerts.
             */
            function initSubscriptionAlerts() {
                var ownedAccount = Account.getOwned(),
                    site = {
                        account: Site.getAccount(),
                        isOwner: Site.isOwner()
                    };

                // Check if we need to show any subscription alerts.
                // 1) If the current user is an account owner, we check that first;
                // 2) Check the current site's account;
                shouldShowAlertsForAccount(ownedAccount).then(function (alertShowing) {
                    // If this is not an account owner, check if any alerts
                    // should show for the current site's account.
                    if (!alertShowing && !site.isOwner) {
                        return shouldShowAlertsForAccount(site.account);
                    }
                });
            }

            /**
             * Show any alerts as needed for a given account.
             * If no account given, no alerts are shown.
             *
             * @param account {object} Account to check for alerts
             */
            function shouldShowAlertsForAccount(account) {
                if (account) {
                    return Subscription.getInfo(account).then(function (subscriptionInfo) {
                        return Subscription.showAlerts(account, subscriptionInfo);
                    });
                }

                return $q.resolve(false);
            }

            bus.subscribe(siteLastDataRefreshed, ({ payload }) => {
                $rootScope.$broadcast('siteLastDataRefreshed', {
                    id: payload.site?.id,
                    last_data: payload.state?.last_data
                });
            });

            bus.subscribe(trackingCodeInstalled, () => {
                $rootScope.$broadcast('TCInstallationCompleted');
            });

            /*************************
             * SCOPE FUNCTIONS
             *************************/
            /**
             * Get plan information.
             */
            $scope.currentSitePlanInfo = function () {
                return Plan.get();
            };

            /**
             * Show the Upsell Modal.
             *
             * Whenever we want to show the upsell modal, we can call this method and pass the
             * referrer (where the request is coming from). Based on that, we may want to overwrite
             * the default title.
             */
            $scope.showUpsellModal = function (referrer) {
                return Subscription.upsell(referrer);
            };

            /**
             * Show the payment details modals.
             */
            $scope.showPaymentModal = function showPaymentModal() {
                const account = Site.getAccount(UserData.currentSite);
                Subscription.updatePaymentMethod(account);
            };

            /**
             * Show the annual upsell modal. This lives in AppCtrl as we want to be
             * able to show this modal anywhere in the app, but with how we declare
             * and bind React components to Angular requires us to have this at this
             * level.
             */
            $scope.showYearlyUpsellModal = function showYearlyUpsellModal(account, referrer) {
                startYearlyUpsell(
                    mapOwnedAccountDtoToModel(account),
                    mapSiteDtoToModel(UserData.currentSite),
                    referrer
                );
            };

            /**
             * This function is only used on the content-container.
             * It seems that ng-attr-id doesn't listen to scope variable changes,
             * and returning the value in a function fixes that.
             */
            $scope.showInterface = function () {
                return $scope.model.showInterface;
            };

            /**
             * Used only for short link widget
             */
            $scope.getCurrentSiteId = () => {
                return UserData.currentSite?.id;
            };

            $scope.onTrackingCodeModalClose = () => {
                $scope.model.verificationModal.visible = false;
            };

            /*************************
             * LISTENERS
             *************************/
            // Check/Update certain values when a user comes back to Hotjar.
            // This event is triggered when a user comes back to Hotjar OR when a user logs in.
            $scope.$on('interfaceLoaded', function () {
                $scope.model.account = Account.getOwned();
                if (UserData.currentSite) {
                    // Initialize / handle any subscription related alerts.
                    initSubscriptionAlerts();

                    UserData.getResources()
                        .then(function () {
                            Organization.users(UserData.currentSite.organization).then(function (users) {
                                $scope.model.organizationUsers = users;
                            }, restDefaultHandlers.onError);
                        }, restDefaultHandlers.onError)
                        .catch(restDefaultHandlers.onError);
                }
            });

            // Re-initialize in case any subscription related alerts should persist.
            $scope.$on('sitePlansChanged', function () {
                initSubscriptionAlerts();
            });

            // Re-set the user object when the properties change.
            $scope.$on('userUpdated', function (event, updatedUser) {
                $scope.model.user = updatedUser;
            });

            // When the state changes, get state properties so we append them
            // as classes to the body.
            $scope.$on('$stateChangeSuccess', function () {
                $timeout(() => {
                    const { title, name, hideFramework } = $state.current;
                    $scope.model.pageState = name.split('.').join('-');
                    $scope.model.pageType = Page.getType();

                    if (
                        window.location.pathname.includes('/surveys/create') ||
                        window.location.pathname.includes('/surveys/edit') ||
                        window.location.pathname.includes('/feedback/create') ||
                        window.location.pathname.includes('/feedback/edit') ||
                        (name === 'react' &&
                            (['/onboarding'].includes(window.location.pathname) ||
                                window.location.pathname.includes('/setup') ||
                                window.location.pathname.includes('/checkout') ||
                                window.location.pathname.includes('/survey-templates')))
                    ) {
                        $scope.model.showInterface = false;
                        useUIState.setState({ isAppInterfaceVisible: $scope.model.showInterface });
                        updateBodyClasses();
                        return;
                    }

                    // This determines weather the "logged in" interface elements should load.
                    $scope.model.showInterface = !(hideFramework || name === '');
                    useUIState.setState({ isAppInterfaceVisible: $scope.model.showInterface });
                    updateBodyClasses();

                    WindowTitle.setTitle(title);
                }, 0);
            });

            useUIState.subscribe(({ isAppInterfaceVisible }) => {
                $scope.model.showInterface = isAppInterfaceVisible;
                updateBodyClasses();
            });

            $scope.$watch(
                () => UserData.currentSite?.organization?.access,
                () => {
                    if (UserData.currentSite?.organization?.access) {
                        setUserAccessBodyProp(UserData.currentSite.organization.access);
                    }
                }
            );

            $scope.$watch(() => UserData.loggedIn, updateBodyClasses);

            // Sidebar has been toggled (open/close)
            $scope.$on('toggledSidebar', function (event, data) {
                useNavigationStore.setState({ isSidebarMinimized: data.minimized });
            });

            useNavigationStore.subscribe(({ isSidebarMinimized }) => {
                $scope.model.sideBarMinimized = isSidebarMinimized;

                updateBodyClasses();
            });

            const updateBodyClasses = () => {
                setBodyClasses({
                    pageState: $scope.model.pageState,
                    pageType: $scope.model.pageType,
                    loggedIn: UserData.loggedIn,
                    sidebarMinimized: $scope.model.sideBarMinimized,
                    showInterface: $scope.model.showInterface
                });
            };
        }
    );
